import { useEffect, useMemo, useState, useRef } from "react";
import { X, SpeakerHigh, Pause, Play, Stop, Translate, SkipBack, SkipForward, CaretDown, CaretUp } from "@phosphor-icons/react";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "bn", label: "বাংলা" },
  { code: "ta", label: "தமிழ்" },
  { code: "te", label: "తెలుగు" },
  { code: "mr", label: "मराठी" },
  { code: "kn", label: "ಕನ್ನಡ" },
  { code: "gu", label: "ગુજરાતી" },
  { code: "pa", label: "ਪੰਜਾਬੀ" },
  { code: "ml", label: "മലയാളം" },
  { code: "or", label: "ଓଡ଼ିଆ" },
  { code: "as", label: "অসমীয়া" },
  { code: "ne", label: "नेपाली" },
  { code: "si", label: "සිංහල" },
  { code: "ur", label: "اردو" },
  { code: "ar", label: "العربية" },
  { code: "fa", label: "فارسی" },
  { code: "he", label: "עברית" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "nl", label: "Nederlands" },
  { code: "sv", label: "Svenska" },
  { code: "no", label: "Norsk" },
  { code: "da", label: "Dansk" },
  { code: "fi", label: "Suomi" },
  { code: "el", label: "Ελληνικά" },
  { code: "pl", label: "Polski" },
  { code: "cs", label: "Čeština" },
  { code: "hu", label: "Magyar" },
  { code: "ro", label: "Română" },
  { code: "bg", label: "Български" },
  { code: "hr", label: "Hrvatski" },
  { code: "sr", label: "Српски" },
  { code: "uk", label: "Українська" },
  { code: "ru", label: "Русский" },
  { code: "tr", label: "Türkçe" },
  { code: "id", label: "Bahasa Indonesia" },
  { code: "ms", label: "Bahasa Melayu" },
  { code: "tl", label: "Filipino" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "th", label: "ไทย" },
  { code: "my", label: "မြန်မာ" },
  { code: "km", label: "ខ្មែរ" },
  { code: "zh", label: "中文" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
  { code: "sw", label: "Kiswahili" },
  { code: "ha", label: "Hausa" },
  { code: "yo", label: "Yorùbá" },
  { code: "zu", label: "isiZulu" },
  { code: "so", label: "Soomaali" },
  { code: "am", label: "አማርኛ" },
];

const RATE_OPTIONS = [
  { key: 0.75, label: "0.75×" },
  { key: 1.0, label: "1×" },
  { key: 1.25, label: "1.25×" },
  { key: 1.5, label: "1.5×" },
  { key: 1.75, label: "1.75×" },
];

// Lightweight built-in browser-side translator using the free public
// endpoint provided by Google Translate. Works entirely client-side and
// requires no API key — perfect for a "quick translate" UX. Rate-limited
// per browser but sufficient for reading an article or observation.
async function browserTranslate(text, target) {
  if (!text || !target || target === "en") return text;
  try {
    const chunks = [];
    // Google's free endpoint caps ~1500 chars per call — split by paragraph.
    const parts = text.match(/[\s\S]{1,1400}(?:\n|\.\s|$)/g) || [text];
    for (const part of parts) {
      const r = await fetch(
        `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=${target}&dt=t&q=${encodeURIComponent(part)}`
      );
      const j = await r.json();
      chunks.push((j?.[0] || []).map((row) => row?.[0] || "").join(""));
    }
    return chunks.join("");
  } catch (e) {
    console.warn("translate failed", e);
    return text;
  }
}

// A compact toolbar used on any long-form surface — pass the text you want
// read/translated. Modal parents pass `variant="pill"` for a rounded chip UI
// or `variant="inline"` for a bordered inline bar.
export default function ListenTranslateBar({
  text = "",
  variant = "pill",
  testidPrefix = "read-tools",
  onTranslated,
}) {
  const [state, setState] = useState("idle"); // idle | playing | paused
  const [progress, setProgress] = useState(0);
  const [lang, setLang] = useState("en");
  const [translating, setTranslating] = useState(false);
  const [showLangs, setShowLangs] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [rate, setRate] = useState(() => {
    try {
      const raw = localStorage.getItem("np:tts-rate");
      const v = raw ? parseFloat(raw) : 1;
      return v >= 0.5 && v <= 2 ? v : 1;
    } catch { return 1; }
  });
  const queueRef = useRef([]);
  const idxRef = useRef(0);
  const canSpeak = typeof window !== "undefined" && "speechSynthesis" in window;

  const cleanText = useMemo(() => (text || "").replace(/\s+/g, " ").trim(), [text]);

  useEffect(() => () => {
    if (canSpeak) window.speechSynthesis.cancel();
  }, [canSpeak]);

  useEffect(() => {
    try { localStorage.setItem("np:tts-rate", String(rate)); } catch { /* noop */ }
  }, [rate]);

  const speakNext = () => {
    const q = queueRef.current;
    const i = idxRef.current;
    if (i >= q.length) { setState("idle"); setProgress(1); return; }
    const u = new SpeechSynthesisUtterance(q[i]);
    u.lang = lang;
    u.rate = rate;
    u.pitch = 1;
    u.onend = () => {
      idxRef.current = i + 1;
      setProgress((i + 1) / q.length);
      speakNext();
    };
    u.onerror = (e) => {
      if (e?.error && e.error !== "canceled" && e.error !== "interrupted") {
        setState("idle");
      }
    };
    window.speechSynthesis.speak(u);
  };

  const play = () => {
    if (!canSpeak || !cleanText) return;
    const s = window.speechSynthesis;
    s.cancel();
    const chunks = cleanText.match(/[\s\S]{1,180}(?:\s|$)/g) || [cleanText];
    queueRef.current = chunks;
    idxRef.current = 0;
    setProgress(0);
    setState("playing");
    setTimeout(() => speakNext(), 40);
  };

  const pause = () => { window.speechSynthesis.pause(); setState("paused"); };
  const resume = () => { window.speechSynthesis.resume(); setState("playing"); };
  const stop = () => {
    window.speechSynthesis.cancel();
    queueRef.current = []; idxRef.current = 0;
    setProgress(0); setState("idle");
  };
  const skip = (delta) => {
    const q = queueRef.current;
    if (!q.length) return;
    const next = Math.min(q.length - 1, Math.max(0, idxRef.current + delta));
    idxRef.current = next;
    setProgress(next / q.length);
    window.speechSynthesis.cancel();
    setState("playing");
    setTimeout(() => speakNext(), 60);
  };
  const applyRate = (v) => {
    setRate(v);
    if (state === "playing" || state === "paused") {
      window.speechSynthesis.cancel();
      setState("playing");
      setTimeout(() => speakNext(), 60);
    }
  };

  const applyLanguage = async (code) => {
    setLang(code);
    setShowLangs(false);
    if (!onTranslated) return;
    if (code === "en") { onTranslated(null); return; }
    setTranslating(true);
    try {
      const translated = await browserTranslate(cleanText, code);
      onTranslated(translated);
    } finally { setTranslating(false); }
  };

  const wrapperCls = variant === "inline"
    ? "flex flex-wrap items-center gap-2 rounded-full border border-black/10 bg-white/70 backdrop-blur px-3 py-1.5"
    : "flex flex-wrap items-center gap-2";

  const chipBase = "inline-flex items-center gap-1.5 rounded-full border font-mono-tech text-[10px] tracking-[0.24em] uppercase transition";
  const activeChip = "bg-[#241209] text-[#F5EBDC] border-[#241209]";
  const idleChip = "bg-white border-black/15 hover:border-black/40 text-[#241209]";

  return (
    <div className={wrapperCls} data-testid={`${testidPrefix}-bar`}>
      {state === "idle" && (
        <button
          type="button"
          onClick={play}
          disabled={!canSpeak}
          data-testid={`${testidPrefix}-listen`}
          title={canSpeak ? "Listen to this" : "Text-to-speech not supported"}
          className={`${chipBase} ${idleChip} px-3 py-1.5 disabled:opacity-40`}
        >
          <SpeakerHigh size={11} weight="fill" /> Listen
        </button>
      )}

      {state !== "idle" && (
        <>
          <button
            type="button"
            onClick={() => skip(-1)}
            data-testid={`${testidPrefix}-skip-back`}
            className={`${chipBase} ${idleChip} px-2.5 py-1.5`}
            aria-label="Skip back"
          >
            <SkipBack size={11} weight="fill" />
          </button>
          {state === "playing" ? (
            <button
              type="button"
              onClick={pause}
              data-testid={`${testidPrefix}-pause`}
              className={`${chipBase} ${activeChip} px-3 py-1.5`}
            >
              <Pause size={11} weight="fill" /> Pause · {Math.round(progress * 100)}%
            </button>
          ) : (
            <button
              type="button"
              onClick={resume}
              data-testid={`${testidPrefix}-resume`}
              className={`${chipBase} ${activeChip} px-3 py-1.5`}
            >
              <Play size={11} weight="fill" /> Resume · {Math.round(progress * 100)}%
            </button>
          )}
          <button
            type="button"
            onClick={() => skip(1)}
            data-testid={`${testidPrefix}-skip-forward`}
            className={`${chipBase} ${idleChip} px-2.5 py-1.5`}
            aria-label="Skip forward"
          >
            <SkipForward size={11} weight="fill" />
          </button>
          <button
            type="button"
            onClick={stop}
            data-testid={`${testidPrefix}-stop`}
            className={`${chipBase} ${idleChip} px-2.5 py-1.5`}
            aria-label="Stop"
          >
            <Stop size={11} weight="fill" />
          </button>
        </>
      )}

      {/* Speed control */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowSettings((v) => !v)}
          data-testid={`${testidPrefix}-speed`}
          className={`${chipBase} ${idleChip} px-2.5 py-1.5`}
        >
          {rate}× {showSettings ? <CaretUp size={9} weight="bold" /> : <CaretDown size={9} weight="bold" />}
        </button>
        {showSettings && (
          <div
            className="absolute z-30 mt-1 rounded-xl border border-black/10 bg-white shadow-lg p-2 flex flex-wrap gap-1"
            data-testid={`${testidPrefix}-speed-menu`}
          >
            {RATE_OPTIONS.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => { applyRate(r.key); setShowSettings(false); }}
                data-testid={`${testidPrefix}-speed-${r.key}`}
                className={`${chipBase} px-2 py-1 ${Math.abs(rate - r.key) < 0.001 ? activeChip : idleChip}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="relative">
        <button
          type="button"
          onClick={() => setShowLangs((v) => !v)}
          data-testid={`${testidPrefix}-translate`}
          className={`${chipBase} ${idleChip} px-3 py-1.5`}
        >
          <Translate size={11} weight="fill" />
          {translating ? "Translating…" : `Translate · ${LANGUAGES.find((l) => l.code === lang)?.label || lang}`}
        </button>
        {showLangs && (
          <div
            className="absolute z-30 mt-1 max-h-64 w-44 overflow-y-auto rounded-xl border border-black/10 bg-white shadow-lg py-1"
            data-testid={`${testidPrefix}-lang-menu`}
          >
            {LANGUAGES.map((l) => (
              <button
                type="button"
                key={l.code}
                onClick={() => applyLanguage(l.code)}
                data-testid={`${testidPrefix}-lang-${l.code}`}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[#F5EBDC] ${
                  lang === l.code ? "font-semibold text-[color:var(--np-brand,#B85F2A)]" : "text-[#241209]"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
